import { Controller, useFormContext } from 'react-hook-form'

import { SolverIcon } from '@/shared/components/Icon/SolverIcon'
import { TimePicker } from '@/shared/components/TimePicker/TimePicker'
import { pxToRem } from '@/shared/styles/themes'
import { Checkbox, FormControlLabel, Grid2 as Grid, Typography } from '@mui/material'

type WorkingHoursTabProps = {
  isReadOnly: boolean
}

export const WorkingHoursTab: React.FC<WorkingHoursTabProps> = ({
  isReadOnly,
}) => {
  const { control } = useFormContext()

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Typography
          variant="body1"
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 500,
            mt: 1,
            mb: 1,
          }}
        >
          Working Hours
        </Typography>
      </Grid>

      {/* Start Time Earliest */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.startTimeEarliest"
          control={control}
          rules={{ required: 'Start time earliest is required' }}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label="Start Time (Earliest)"
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.startTimeEarliestNextDay"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 3 }}
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={isReadOnly}
                />
              }
              label="Start Time Earliest Next Day?"
            />
          )}
        />
      </Grid>

      {/* Start Time Latest */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.startTimeLatest"
          control={control}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label={<><SolverIcon />Start Time (Latest)</>}
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.startTimeLatestNextDay"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 3 }}
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={isReadOnly}
                />
              }
              label="Start Time Latest Next Day?"
            />
          )}
        />
      </Grid>

      {/* Finish Time Latest */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.finishTimeLatest"
          control={control}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label={<><SolverIcon />Finish Time (Latest)</>}
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.finishTimeLatestNextDay"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 3 }}
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={isReadOnly}
                />
              }
              label="Finish Time Latest Next Day?"
            />
          )}
        />
      </Grid>

      {/* Load Time Earliest */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.loadTimeEarliest"
          control={control}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label={<><SolverIcon />Load Time (Earliest)</>}
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.loadTimeEarliestNextDay"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 3 }}
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={isReadOnly}
                />
              }
              label="Load Time Earliest Next Day?"
            />
          )}
        />
      </Grid>

      {/* Load Time Latest */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.loadTimeLatest"
          control={control}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label={<><SolverIcon />Load Time (Latest)</>}
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.loadTimeLatestNextDay"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 3 }}
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={isReadOnly}
                />
              }
              label="Load Time Latest Next Day?"
            />
          )}
        />
      </Grid>

      {/* Max Times */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.maxDrivingTime"
          control={control}
          rules={{
            required: 'Maximum driving time is required',
            validate: (value, formValues) => {
              if (value > formValues.workDay.maxWorkingTime) {
                return 'Max driving time cannot be greater than max working time'
              }

              return true
            },
          }}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label="Maximum Driving Time"
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.maxWorkingTime"
          control={control}
          rules={{
            required: 'Maximum working time is required',
            validate: (value, formValues) => {
              if (value < formValues.workDay.maxDrivingTime) {
                return 'Max working time cannot be less than max driving time'
              }

              return true
            },
          }}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label="Maximum Working Time"
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="workDay.minPaidTime"
          control={control}
          render={({ field, fieldState }) => (
            <TimePicker
              {...field}
              label={<><SolverIcon />Minimum Paid Time</>}
              disabled={isReadOnly}
              format="HH:mm"
              views={['hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'HH:mm',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  )
}